import React, { useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    isPodscopeWaitlistYouTubeUrlAllowed,
    PODSCOPE_WAITLIST_YOUTUBE_URL_INVALID_MESSAGE,
    submitPodscopeProfileWaitlist,
} from '../api/podscopeWaitlist';
import { glassDialogPaperSx, textFieldDarkFieldErrorSx } from '../components/dialogDarkGlass';
import { MAIN_BG_COLOR } from '../theme';

const WAITLIST_REQUIRED_FIELD_MESSAGE = 'Completa este campo.';
const WAITLIST_EMAIL_INVALID_MESSAGE = 'Introduce un correo electrónico válido.';

type PodscopeWaitlistFieldKey = 'name' | 'email' | 'cargo' | 'youtubeChannelUrl';

function emptyPodscopeWaitlistFieldErrors(): Record<PodscopeWaitlistFieldKey, string | null> {
    return {
        name: null,
        email: null,
        cargo: null,
        youtubeChannelUrl: null,
    };
}

function isWaitlistEmailFormatValid(value: string): boolean {
    const t = value.trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t);
}

function getErrorMessage(err: unknown): string {
    if (err && typeof err === 'object' && 'response' in err) {
        const data = (err as { response?: { data?: { message?: string } } }).response?.data;
        if (data?.message) return data.message;
    }
    return 'No pudimos enviar el formulario. Intenta de nuevo más tarde.';
}

function getAxiosErrorPayload(err: unknown): {
    status?: number;
    message?: string;
    fields?: string[];
} | null {
    if (err && typeof err === 'object' && 'response' in err) {
        const res = (
            err as {
                response?: {
                    status?: number;
                    data?: { message?: string; fields?: string[] };
                };
            }
        ).response;
        if (!res) return null;
        return {
            status: res.status,
            message: res.data?.message,
            fields: res.data?.fields,
        };
    }
    return null;
}

const alertErrorSx = {
    mb: 2,
    bgcolor: 'rgba(244, 67, 54, 0.15)',
    color: '#ffcdd2',
    border: '1px solid rgba(239, 83, 80, 0.45)',
    '& .MuiAlert-icon': { color: '#ef9a9a' },
};

const profilePreviewSrc = `${process.env.PUBLIC_URL ?? ''}/perfil-podscope.png`;

const PodscopeProfileWaitlistPage: React.FC = () => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [cargo, setCargo] = useState('');
    const [youtubeChannelUrl, setYoutubeChannelUrl] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState(emptyPodscopeWaitlistFieldErrors);
    const [previewOpen, setPreviewOpen] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorMessage(null);
        setFieldErrors(emptyPodscopeWaitlistFieldErrors());
        setSuccessMessage(null);

        const next = emptyPodscopeWaitlistFieldErrors();
        let invalid = false;

        if (!name.trim()) {
            next.name = WAITLIST_REQUIRED_FIELD_MESSAGE;
            invalid = true;
        }
        if (!email.trim()) {
            next.email = WAITLIST_REQUIRED_FIELD_MESSAGE;
            invalid = true;
        } else if (!isWaitlistEmailFormatValid(email)) {
            next.email = WAITLIST_EMAIL_INVALID_MESSAGE;
            invalid = true;
        }
        if (!cargo.trim()) {
            next.cargo = WAITLIST_REQUIRED_FIELD_MESSAGE;
            invalid = true;
        }
        if (!youtubeChannelUrl.trim()) {
            next.youtubeChannelUrl = WAITLIST_REQUIRED_FIELD_MESSAGE;
            invalid = true;
        } else if (!isPodscopeWaitlistYouTubeUrlAllowed(youtubeChannelUrl)) {
            next.youtubeChannelUrl = PODSCOPE_WAITLIST_YOUTUBE_URL_INVALID_MESSAGE;
            invalid = true;
        }

        if (invalid) {
            setFieldErrors(next);
            return;
        }

        setSubmitting(true);
        try {
            const data = await submitPodscopeProfileWaitlist({
                name,
                email,
                cargo,
                youtubeChannelUrl,
            });
            setSuccessMessage(
                data.message ?? 'Te agregamos a la lista de espera. Te contactaremos pronto.'
            );
            setName('');
            setEmail('');
            setCargo('');
            setYoutubeChannelUrl('');
            setFieldErrors(emptyPodscopeWaitlistFieldErrors());
        } catch (err: unknown) {
            const payload = getAxiosErrorPayload(err);
            const msg = payload?.message ?? getErrorMessage(err);

            if (payload?.status === 409) {
                setFieldErrors({
                    ...emptyPodscopeWaitlistFieldErrors(),
                    email: msg,
                });
                return;
            }

            if (payload?.fields?.length) {
                const fe = emptyPodscopeWaitlistFieldErrors();
                for (const f of payload.fields) {
                    if (f === 'name') fe.name = WAITLIST_REQUIRED_FIELD_MESSAGE;
                    if (f === 'email') fe.email = WAITLIST_REQUIRED_FIELD_MESSAGE;
                    if (f === 'cargo') fe.cargo = WAITLIST_REQUIRED_FIELD_MESSAGE;
                    if (f === 'youtubeChannelUrl') {
                        fe.youtubeChannelUrl = WAITLIST_REQUIRED_FIELD_MESSAGE;
                    }
                }
                setFieldErrors(fe);
                return;
            }

            if (msg === PODSCOPE_WAITLIST_YOUTUBE_URL_INVALID_MESSAGE) {
                setFieldErrors({
                    ...emptyPodscopeWaitlistFieldErrors(),
                    youtubeChannelUrl: msg,
                });
                return;
            }

            setErrorMessage(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box
            sx={{
                bgcolor: MAIN_BG_COLOR,
                minHeight: { xs: 'calc(100dvh - 56px)', md: 'calc(100dvh - 64px)' },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'stretch',
                py: { xs: 3, sm: 4 },
                px: { xs: 2, sm: 3 },
                boxSizing: 'border-box',
            }}
        >
            <Container maxWidth="md" disableGutters sx={{ width: '100%', mx: 'auto' }}>
                <Stack spacing={{ xs: 5, sm: 6 }}>
                    <Box>
                        <Typography
                            variant="h3"
                            component="h1"
                            gutterBottom
                            sx={{
                                fontWeight: 700,
                                fontFamily: '"DM Serif Display", serif',
                                color: 'white',
                                fontSize: { xs: '2rem', sm: '2.375rem' },
                                lineHeight: 1.15,
                                mb: { xs: 2, sm: 2.5 },
                            }}
                        >
                            Perfil Podscope
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                mb: { xs: 3.5, sm: 4 },
                                color: 'rgba(255, 255, 255, 0.88)',
                                lineHeight: 1.55,
                                fontSize: { xs: '1.0625rem', sm: '1.125rem' },
                            }}
                        >
                            Sigue a tu audiencia y rendimiento en YouTube, Instagram y TikTok,
                            además de tus programas según el Ranking Podscope.
                        </Typography>

                        <Box
                            role="button"
                            tabIndex={0}
                            aria-label="Ver imagen en pantalla completa"
                            onClick={() => setPreviewOpen(true)}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setPreviewOpen(true);
                                }
                            }}
                            sx={{
                                borderRadius: 2,
                                overflow: 'hidden',
                                border: '1px solid rgba(255, 255, 255, 0.14)',
                                bgcolor: 'rgba(10, 12, 18, 0.55)',
                                boxShadow: `
                                    0 2px 4px rgba(0, 0, 0, 0.35),
                                    0 16px 40px rgba(0, 0, 0, 0.55),
                                    0 40px 80px rgba(0, 0, 0, 0.35),
                                    inset 0 1px 0 rgba(255, 255, 255, 0.06)
                                `,
                                cursor: 'pointer',
                                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: `
                                        0 4px 8px rgba(0, 0, 0, 0.4),
                                        0 20px 48px rgba(0, 0, 0, 0.5),
                                        0 48px 96px rgba(0, 0, 0, 0.38),
                                        inset 0 1px 0 rgba(255, 255, 255, 0.08)
                                    `,
                                },
                                '&:focus-visible': {
                                    outline: '2px solid rgba(255, 255, 255, 0.55)',
                                    outlineOffset: 3,
                                },
                            }}
                        >
                            <Box
                                component="img"
                                src={profilePreviewSrc}
                                alt="Ejemplo de cómo se verá tu perfil en Podscope"
                                sx={{
                                    display: 'block',
                                    width: '100%',
                                    height: 'auto',
                                    verticalAlign: 'bottom',
                                    pointerEvents: 'none',
                                }}
                            />
                        </Box>
                    </Box>

                    {errorMessage && (
                        <Alert severity="error" sx={alertErrorSx}>
                            {errorMessage}
                        </Alert>
                    )}

                    <Box>
                        <Typography
                            variant="subtitle1"
                            component="h2"
                            sx={{
                                fontWeight: 700,
                                fontFamily: '"DM Serif Display", serif',
                                color: 'white',
                                mb: 1.5,
                                fontSize: { xs: '1.15rem', sm: '1.3rem' },
                                lineHeight: 1.35,
                            }}
                        >
                            Reclama tu Perfil Podscope en cuanto salga
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(255, 255, 255, 0.72)',
                                lineHeight: 1.65,
                                fontSize: { xs: '1.0625rem', sm: '1.125rem' },
                            }}
                        >
                            Estamos habilitando el perfil por etapas. Si completas el formulario de
                            abajo, te contactaremos cuando puedas activar tu perfil.
                        </Typography>
                    </Box>

                    <Paper
                        elevation={0}
                        sx={{
                            ...glassDialogPaperSx,
                            p: { xs: 3, sm: 4 },
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            component="h3"
                            sx={{
                                fontWeight: 700,
                                fontFamily: '"DM Serif Display", serif',
                                color: 'white',
                                fontSize: '1.25rem',
                                mb: 2.5,
                                mt: -0.25,
                            }}
                        >
                            Déjanos tu información
                        </Typography>
                        <Box component="form" onSubmit={handleSubmit} noValidate>
                            <TextField
                                fullWidth
                                required
                                margin="normal"
                                label="Nombre"
                                name="name"
                                value={name}
                                onChange={e => {
                                    setSuccessMessage(null);
                                    setName(e.target.value);
                                    setFieldErrors(prev =>
                                        prev.name ? { ...prev, name: null } : prev
                                    );
                                }}
                                autoComplete="name"
                                disabled={submitting}
                                error={Boolean(fieldErrors.name)}
                                helperText={fieldErrors.name ?? undefined}
                                sx={{ mt: 0, ...textFieldDarkFieldErrorSx }}
                            />
                            <TextField
                                fullWidth
                                required
                                margin="normal"
                                type="email"
                                label="Email"
                                name="email"
                                value={email}
                                onChange={e => {
                                    setSuccessMessage(null);
                                    setEmail(e.target.value);
                                    setFieldErrors(prev =>
                                        prev.email ? { ...prev, email: null } : prev
                                    );
                                }}
                                autoComplete="email"
                                disabled={submitting}
                                error={Boolean(fieldErrors.email)}
                                helperText={fieldErrors.email ?? undefined}
                                sx={textFieldDarkFieldErrorSx}
                            />
                            <TextField
                                fullWidth
                                required
                                margin="normal"
                                label="Cargo"
                                name="cargo"
                                value={cargo}
                                onChange={e => {
                                    setSuccessMessage(null);
                                    setCargo(e.target.value);
                                    setFieldErrors(prev =>
                                        prev.cargo ? { ...prev, cargo: null } : prev
                                    );
                                }}
                                autoComplete="organization-title"
                                disabled={submitting}
                                error={Boolean(fieldErrors.cargo)}
                                helperText={fieldErrors.cargo ?? undefined}
                                sx={textFieldDarkFieldErrorSx}
                            />
                            <TextField
                                fullWidth
                                required
                                margin="normal"
                                label="Link del canal de YouTube"
                                name="youtubeChannelUrl"
                                value={youtubeChannelUrl}
                                onChange={e => {
                                    setSuccessMessage(null);
                                    setYoutubeChannelUrl(e.target.value);
                                    setFieldErrors(prev =>
                                        prev.youtubeChannelUrl
                                            ? { ...prev, youtubeChannelUrl: null }
                                            : prev
                                    );
                                }}
                                placeholder="https://www.youtube.com/@tu-canal"
                                disabled={submitting}
                                error={Boolean(fieldErrors.youtubeChannelUrl)}
                                helperText={fieldErrors.youtubeChannelUrl ?? undefined}
                                sx={textFieldDarkFieldErrorSx}
                            />
                            <Box sx={{ mt: 3 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    disabled={submitting || Boolean(successMessage)}
                                    fullWidth
                                    sx={{
                                        ...(successMessage
                                            ? {
                                                  backgroundColor: 'rgba(76, 175, 80, 0.28)',
                                                  color: '#e8f5e9',
                                                  border: '1px solid rgba(129, 199, 132, 0.5)',
                                                  '&:hover': {
                                                      backgroundColor: 'rgba(76, 175, 80, 0.36)',
                                                      boxShadow: 'none',
                                                  },
                                                  '&.Mui-disabled': {
                                                      backgroundColor: 'rgba(76, 175, 80, 0.28)',
                                                      color: '#e8f5e9',
                                                      borderColor: 'rgba(129, 199, 132, 0.5)',
                                                  },
                                              }
                                            : {
                                                  backgroundColor: 'rgba(255,255,255,0.14)',
                                                  color: 'white',
                                                  border: '1px solid rgba(255,255,255,0.22)',
                                                  '&:hover': {
                                                      backgroundColor: 'rgba(255,255,255,0.22)',
                                                      boxShadow: 'none',
                                                  },
                                                  '&.Mui-disabled': {
                                                      backgroundColor: 'rgba(255,255,255,0.06)',
                                                      color: 'rgba(255,255,255,0.35)',
                                                      borderColor: 'rgba(255,255,255,0.1)',
                                                  },
                                              }),
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        py: 1.25,
                                        borderRadius: '10px',
                                        boxShadow: 'none',
                                    }}
                                >
                                    {submitting ? (
                                        <CircularProgress size={24} sx={{ color: 'white' }} />
                                    ) : successMessage ? (
                                        <>
                                            <CheckCircleIcon
                                                sx={{
                                                    mr: 1,
                                                    fontSize: 22,
                                                    verticalAlign: 'middle',
                                                }}
                                            />
                                            Listo
                                        </>
                                    ) : (
                                        'Unirme a la lista'
                                    )}
                                </Button>
                                {successMessage ? (
                                    <Typography
                                        component="p"
                                        variant="body2"
                                        sx={{
                                            mt: 1.5,
                                            mb: 0,
                                            textAlign: 'center',
                                            color: 'rgba(200, 230, 201, 0.92)',
                                            lineHeight: 1.55,
                                        }}
                                    >
                                        {successMessage}
                                    </Typography>
                                ) : null}
                            </Box>
                        </Box>
                    </Paper>
                </Stack>
            </Container>

            <Dialog
                fullScreen
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                PaperProps={{
                    sx: {
                        bgcolor: 'rgba(6, 7, 12, 0.97)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    },
                }}
            >
                <IconButton
                    type="button"
                    aria-label="Cerrar vista ampliada"
                    onClick={() => setPreviewOpen(false)}
                    sx={{
                        position: 'fixed',
                        top: 12,
                        right: 12,
                        zIndex: 2,
                        color: 'white',
                        bgcolor: 'rgba(255,255,255,0.08)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' },
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <Box
                    component="img"
                    src={profilePreviewSrc}
                    alt=""
                    sx={{
                        maxWidth: '100vw',
                        maxHeight: '100vh',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                        p: { xs: 2, sm: 4 },
                        boxSizing: 'border-box',
                    }}
                />
            </Dialog>
        </Box>
    );
};

export default PodscopeProfileWaitlistPage;
